'use client';
import { useState, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';

// ── Types ──────────────────────────────────────────────────────────────────
interface Tool {
  id: string;
  slug: string;
  name: string;
  category: 'Free Tools' | 'Pro Tools';
  type: 'Chrome Extension' | 'Web App' | 'Desktop App';
  shortDescription: string;
  featuresFree: string[];
  featuresPro?: string[];
  tags: string[];
}

type FilterCategory = 'All' | 'Free Tools' | 'Pro Tools';

// ── Tool Data ──────────────────────────────────────────────────────────────
const TOOLS: Tool[] = [
  {
    id: 'x-cleanup-console',
    slug: 'x-cleanup-console',
    name: 'X Cleanup Console',
    category: 'Free Tools',
    type: 'Chrome Extension',
    shortDescription:
      'Bulk-delete your posts on X faster with preview mode, smart controls, and optional Pro filters.',
    featuresFree: [
      'Preview visible posts',
      'Bulk delete from visible timeline',
      'Skip pinned post',
      'Delay controls',
      '25 deletes per day',
    ],
    featuresPro: [
      'Keyword include filter',
      'Keyword exclude filter',
      'Delete before date',
      'Delete after date',
      'Include or exclude replies',
      'Include or exclude reposts',
      'Higher-volume cleanup',
      'License activation',
    ],
    tags: ['X', 'Twitter', 'Chrome Extension', 'Cleanup', 'Social Media', 'Reputation', 'Productivity'],
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  'Free Tools': 'bg-emerald-950 text-emerald-300 border border-emerald-800',
  'Pro Tools': 'bg-purple-950 text-purple-300 border border-purple-800',
};

const TYPE_COLORS: Record<string, string> = {
  'Chrome Extension': 'bg-[#0a0f1a] text-sky-400 border border-sky-900',
  'Web App': 'bg-[#0a1a0a] text-green-400 border border-green-900',
  'Desktop App': 'bg-[#1a0a0a] text-red-400 border border-red-900',
};

// ── Tool Card ──────────────────────────────────────────────────────────────
interface ToolCardProps {
  tool: Tool;
}

function ToolCard({ tool }: ToolCardProps) {
  return (
    <div className="border border-[#1e1e1e] bg-[#070707] hover:border-[#2e2e2e] transition-all duration-200 flex flex-col group">
      {/* Card Header */}
      <div className="p-6 pb-4 border-b border-[#141414]">
        <div className="flex items-start justify-between gap-3 mb-4">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${CATEGORY_COLORS[tool.category]}`}>
            {tool.category}
          </span>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 shrink-0 ${TYPE_COLORS[tool.type]}`}>
            {tool.type}
          </span>
        </div>
        <h3 className="text-base font-bold leading-snug group-hover:text-primary transition-colors">
          {tool.name}
        </h3>
      </div>

      {/* Description */}
      <div className="px-6 py-4 flex-1">
        <p className="text-[#888] text-sm leading-relaxed mb-5">
          {tool.shortDescription}
        </p>

        {/* Free Features */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#555] font-semibold mb-3">Free Features</p>
          <ul className="space-y-2">
            {tool.featuresFree.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-xs text-[#aaa]">
                <svg className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Pro Features teaser */}
        {tool.featuresPro && tool.featuresPro.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#141414]">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#555] font-semibold mb-2">
              Pro Features Available
            </p>
            <p className="text-xs text-[#666]">
              {tool.featuresPro.length} advanced filters &amp; controls — see full details on the tool page.
            </p>
          </div>
        )}
      </div>

      {/* CTA Button */}
      <div className="px-6 pb-6 pt-4">
        <Link href={`/tools/${tool.slug}`} className="block">
          <button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-4 text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            Learn More
          </button>
        </Link>
      </div>
    </div>
  );
}

// ── Main Tools Page ────────────────────────────────────────────────────────
export default function ToolsPage() {
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const FILTER_TABS: FilterCategory[] = ['All', 'Free Tools', 'Pro Tools'];

  const filteredTools = useMemo(() => {
    return TOOLS.filter((t) => {
      const matchesCategory = activeFilter === 'All' || t.category === activeFilter;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.shortDescription.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.type.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q)) ||
        t.featuresFree.some((f) => f.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">

        {/* ── Hero ── */}
        <section className="py-20 px-6 border-b border-[#1a1a1a]">
          <div className="max-w-[1100px] mx-auto">
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-4 font-semibold">Tools — Built by Daniel Moravec</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Tools That Actually Do Something.<br />
              <span className="text-muted-foreground">Built by the founder. For operators.</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-[680px] mb-6">
              These are real tools Daniel Moravec personally built — not outsourced, not vaporware. Each one solves a specific problem he encountered as an operator, trader, and content creator. Built using AI as leverage, shipped as working products.
            </p>
            <p className="text-[#888] text-base max-w-[680px] mb-4">
              Tools like these are available privately — some free, some through subscription. The ones listed here are the public-facing versions. If you're interested in private access or custom tooling, <a href="/work-with-daniel" className="text-primary hover:underline">work directly with Daniel</a>.
            </p>
            <p className="text-[#888] text-base max-w-[680px] mb-8">
              No accounts required to get started on free tools. Click any tool to see full details, features, and the download link.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4 mt-2">
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">{TOOLS.length}</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Tool{TOOLS.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-primary">Free</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">To Start</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">Chrome</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Extensions</p>
              </div>
              <div className="w-px h-8 bg-[#2a2a2a] shrink-0" />
              <div className="text-center shrink-0">
                <p className="text-2xl font-bold text-white">Daniel</p>
                <p className="text-[11px] text-[#666] uppercase tracking-wider">Built These</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Filter + Search Bar ── */}
        <section className="sticky top-[72px] z-30 bg-[#050505] border-b border-[#1a1a1a] px-6 py-4 overflow-hidden w-full">
          <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Category Tabs */}
            <div className="w-full flex items-center gap-1 overflow-x-auto flex-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveFilter(tab)}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    activeFilter === tab
                      ? 'bg-primary text-white' :'text-[#666] hover:text-white border border-[#2a2a2a] hover:border-[#444]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative sm:ml-auto w-full sm:w-64">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools..."
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white placeholder-[#444] pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[#444] transition-colors"
              />
            </div>
          </div>
        </section>

        {/* ── Tools Grid ── */}
        <section className="py-16 px-6">
          <div className="max-w-[1100px] mx-auto">
            {filteredTools.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-[#555] text-lg">No tools match your search.</p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveFilter('All'); }}
                  className="mt-4 text-xs text-primary uppercase tracking-wider hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#555] uppercase tracking-wider mb-8">
                  {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''}
                  {activeFilter !== 'All' ? ` in ${activeFilter}` : ''}
                  {searchQuery ? ` matching "${searchQuery}"` : ''}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredTools.map((tool) => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── More Tools Coming ── */}
        <section className="py-20 px-6 border-t border-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto">
            <div className="border border-primary/30 p-10 bg-[#080808] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3 font-semibold">More coming soon</p>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  We Build Tools the Same Way We Build Everything Else.
                </h2>
                <p className="text-[#aaa] text-base mb-6 max-w-[640px]">
                  Every tool in this library started as an internal solution to a real problem. When something works well enough to share, we package it and put it here. More tools are in development — check back regularly.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Built for real use cases</p>
                      <p className="text-[#666] text-xs">Not demos — tools we actually use internally</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Free tiers that actually work</p>
                      <p className="text-[#666] text-xs">No crippled free versions designed to frustrate you</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">Pro upgrades for power users</p>
                      <p className="text-[#666] text-xs">Advanced filters and controls when you need more</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-primary text-lg mt-0.5 shrink-0">✓</span>
                    <div>
                      <p className="text-white text-sm font-semibold">No accounts required to start</p>
                      <p className="text-[#666] text-xs">Download and use — no sign-up walls</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link href="/diy">
                    <Button variant="primary" size="lg">Explore Free Templates Too</Button>
                  </Link>
                  <Link href="/services">
                    <Button variant="secondary" size="lg">View Full Services</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="py-16 px-6 border-t border-[#1a1a1a] bg-[#050505]">
          <div className="max-w-[760px] mx-auto text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4 font-semibold">When you're ready</p>
            <h2 className="text-2xl font-bold mb-4">Need more than a tool?</h2>
            <p className="text-muted-foreground mb-8 max-w-[480px] mx-auto">
              When you need a full team behind your marketing, strategy, and execution — we&apos;d love to hear from you.
            </p>
            <Link href="/book">
              <Button variant="secondary" size="lg">Apply to Work With Us</Button>
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
